import { useState } from 'react'
import {
  Alert, Badge, Button, Code, Group, Modal, Skeleton, Stack, Text, TextInput,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconAdjustments, IconAlertTriangle, IconKey, IconPencil, IconPlayerPause, IconPower,
} from '@tabler/icons-react'
import { Link, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { Callout, DataCard } from '@/components/ui'
import { useCanNaPlataforma } from '@/store/auth'
import {
  AVATAR_LABEL, CHAVE_DETALHE, CHAVE_FROTA, HARDWARE_COM_AVATAR, MODELO_LABEL,
  alterarRobo, aposentarRobo, codigoDaRecusa, enderecoDoWifiDaFrota, provisionarRobo,
  rotacionarCredencial, vincularPlacaAoCadastro,
  useNotificacoesDoDono, useRedePadrao, useReleasesDeFirmware, useRoboDetalhe, type CredencialEmitida,
  type HardwareModel,
} from '@/api/robo'
import type { Subscription } from '@/api/notification-preferences'
import { SEVERITY_OPTIONS, sourceLabel } from '../notificacoes/preferencias-constants'
import { ConfigurarPelaUsb } from '@/features/robo-usb/ConfigurarPelaUsb'
import { DiagnosticoPelaUsb } from '@/features/robo-usb/DiagnosticoPelaUsb'
import { GravarFirmware } from '@/features/robo-usb/GravarFirmware'
import { AtribuirDono } from './AtribuirDono'
import { AtualizarPeloAr } from './AtualizarPeloAr'
import { ComecarDeNovo } from './ComecarDeNovo'
import { nomeOuFallback } from './nomeDoRobo'
import { CredencialModal } from './CredencialModal'
import { FirmwareSelo } from './FirmwareSelo'
import { SeletorDeLocal } from './SeletorDeLocal'
import { SeletorDeModelo } from './SeletorDeModelo'
import { VersoesDoModelo } from './VersoesDoModelo'
import { WifiDoRobo } from './WifiDoRobo'
import { avisoDeCredencial, estadoDoRobo } from './estado'

/** Rotulo a esquerda, valor a esquerda — alinhamento a esquerda nos dois. */
function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <Group align="flex-start" wrap="nowrap" gap="md">
      <Text size="sm" c="dimmed" w={190} style={{ flexShrink: 0 }}>{rotulo}</Text>
      <Stack gap={2}>{children}</Stack>
    </Group>
  )
}

/**
 * O escopo e' do DONO — resolver o nome exigiria os tenants/orgs DELE, nao os
 * de quem administra a frota (podem nao ser os mesmos). Mostrar o id cru e'
 * honesto; inventar um nome errado nao seria.
 */
function rotuloEscopoSomenteLeitura(s: Subscription): string {
  if (!s.scope_kind || s.scope_id == null) return 'Tudo que ele recebe'
  return s.scope_kind === 'tenant' ? `Tenant #${s.scope_id}` : `Organização #${s.scope_id}`
}

export function RoboDetalhe() {
  const { publicId } = useParams()
  // A frota e' da plataforma (spec 2026-09-24, D1): a permissao e' GLOBAL,
  // a mesma pergunta que o backend faz. Nem o seletor do topo nem o tenant
  // legado do robo entram na conta.
  const can = useCanNaPlataforma()
  const qc = useQueryClient()
  const { data: robo, isLoading } = useRoboDetalhe(publicId)
  const { data: modelos } = useReleasesDeFirmware()
  // A rede padrão só é oferecida a quem a pode levar: a rota pede `robo:manage`
  // global além do `rotate` (spec 2026-09-29-robo-wifi-padrao, 4.5).
  const redePadrao = useRedePadrao(can('robo', 'manage'))
  const { data: assinaturasDoDono, isLoading: carregandoNotificacoes } =
    useNotificacoesDoDono(publicId)
  const notificacoesAtivas = (assinaturasDoDono ?? []).filter((s) => s.enabled)

  const [confirmarAposentar, ctlAposentar] = useDisclosure(false)
  const [editando, ctlEditar] = useDisclosure(false)
  const [emitida, setEmitida] = useState<CredencialEmitida | null>(null)
  const [rascunho, setRascunho] = useState<{
    name: string; location: string; modelo: HardwareModel | null
  }>({ name: '', location: '', modelo: null })

  const recarrega = () => {
    qc.invalidateQueries({ queryKey: CHAVE_FROTA })
    qc.invalidateQueries({ queryKey: CHAVE_DETALHE(publicId) })
  }

  if (isLoading) return <Text size="sm" c="dimmed">Carregando…</Text>
  if (!robo) return <Text size="sm" c="dimmed">Robô não encontrado.</Text>

  const agora = Date.now()
  const e = estadoDoRobo(robo)
  const aviso = avisoDeCredencial(robo, agora)
  const podeGerir = can('robo', 'manage')

  return (
    <Stack gap="md">
      {/* Sem titulo proprio: o AppLayout ja' escreve "Detalhe do robô" acima, e
          dois titulos empilhados sao exatamente o defeito que a guarda
          `AppLayout.cabecalho.test.tsx` existe para evitar. O NOME do robo vai
          para o titulo do cartao, onde identifica o que esta sendo lido. */}
      <Group justify="flex-end">
        <Group gap="xs">
          {/* A tela de ajustes (spec 2026-09-24-robo-painel-de-ajustes): mesma porta
              das rotas dela, `robo:configure` global. */}
          {can('robo', 'configure') && robo.status !== 'retired' && (
            <Button
              variant="default"
              leftSection={<IconAdjustments size={16} />}
              component={Link}
              to={`/robo/frota/${encodeURIComponent(robo.public_id)}/ajustes`}
            >
              Ajustes
            </Button>
          )}
          {podeGerir && robo.status !== 'retired' && (
            <Button
              variant="default"
              leftSection={<IconPencil size={16} />}
              onClick={() => {
                setRascunho({ name: robo.name, location: robo.location ?? '', modelo: robo.hardware_model })
                ctlEditar.open()
              }}
            >
              Editar
            </Button>
          )}
          {podeGerir && robo.status !== 'retired' && (
            <Button
              variant="default"
              leftSection={<IconPlayerPause size={16} />}
              onClick={async () => {
                await alterarRobo(robo.public_id, {
                  status: robo.status === 'suspended' ? 'active' : 'suspended',
                })
                recarrega()
              }}
            >
              {robo.status === 'suspended' ? 'Reativar' : 'Suspender'}
            </Button>
          )}
          {can('robo', 'rotate') && robo.status !== 'retired' && (
            <Button
              variant="default"
              leftSection={<IconKey size={16} />}
              onClick={async () => {
                setEmitida(await rotacionarCredencial(robo.public_id))
                recarrega()
              }}
            >
              Rotacionar credencial
            </Button>
          )}
          {podeGerir && robo.status !== 'retired' && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconPower size={16} />}
              onClick={ctlAposentar.open}
            >
              Aposentar
            </Button>
          )}
        </Group>
      </Group>

      {aviso.chave === 'vencida' && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Credencial vencida">
          O robô não consegue mais autenticar no Console. Rotacionar emite uma nova.
        </Alert>
      )}

      <DataCard title={robo.location
        ? `${nomeOuFallback(robo)} — ${robo.location}`
        : nomeOuFallback(robo)}>
        <Stack gap="sm">
          <Linha rotulo="Estado">
            <Badge color={e.cor} variant="light">{e.rotulo}</Badge>
            <Text size="xs" c="dimmed">{e.significado}</Text>
          </Linha>
          <Linha rotulo="Identificador"><Code>{robo.public_id}</Code></Linha>
          {/* So' o robo legado tem tenant (a Ivy, firmware antigo). O robo da
              plataforma nao tem cliente, e a linha nao inventa um: o que ele
              mostra passa a ser escolha do dono (Parte B). */}
          {robo.tenant_id != null && (
            <Linha rotulo="Cliente legado">
              <Text size="sm">{robo.tenant_nome ?? `tenant #${robo.tenant_id}`}</Text>
            </Linha>
          )}
          <Linha rotulo="Última comunicação">
            <Text size="sm">
              {robo.last_seen_at
                ? new Date(robo.last_seen_at).toLocaleString('pt-BR')
                : 'Nunca falou com o Console'}
            </Text>
          </Linha>
          <Linha rotulo="Firmware">
            <FirmwareSelo firmware={robo.firmware} bruto={robo.firmware_version} />
          </Linha>
          <Linha rotulo="Modelo">
            <Text size="sm">
              {robo.hardware_model ? MODELO_LABEL[robo.hardware_model] : 'Não informado'}
            </Text>
          </Linha>
          {/* Avatar só existe no Robô II, e é SOMENTE LEITURA aqui: quem escolhe
              é o dono, pela tela dele. Mesma simetria das notificações abaixo —
              quem administra a frota enxerga para dar suporte, não configura. */}
          {robo.hardware_model === HARDWARE_COM_AVATAR && (
            <Linha rotulo="Avatar">
              <Text size="sm">
                {robo.avatar ? AVATAR_LABEL[robo.avatar] : 'Inovare (padrão)'}
              </Text>
            </Linha>
          )}
          <Linha rotulo="Credencial">
            <Text size="sm">
              {aviso.chave === 'sem_credencial'
                ? 'Não emitida'
                : aviso.chave === 'vencida'
                  ? 'Vencida'
                  : `Vence em ${aviso.diasRestantes} ${aviso.diasRestantes === 1 ? 'dia' : 'dias'}`}
            </Text>
          </Linha>
        </Stack>
      </DataCard>

      {modelos && <VersoesDoModelo firmware={robo.firmware} modelos={modelos} podeHomologar={podeGerir} />}

      {podeGerir && (
        <DataCard title="Dono">
          <Stack gap="sm">
            {robo.owner_user_id && robo.assigned_at && (
              <Text size="xs" c="dimmed">
                Atribuído{robo.atribuido_por_nome ? ` por ${robo.atribuido_por_nome}` : ''} em{' '}
                {new Date(robo.assigned_at).toLocaleDateString('pt-BR')}
              </Text>
            )}
            <AtribuirDono robo={robo} onMudou={recarrega} />
          </Stack>
        </DataCard>
      )}

      {can('robo', 'rotate') && robo.status !== 'retired' && (
        <DataCard title="USB">
          <ConfigurarPelaUsb robo={robo} aoConcluir={recarrega}
            redePadrao={redePadrao.data?.ssid ?? null}
            pedirCredencial={(_mac, comPadrao) =>
              provisionarRobo(robo.public_id, { comWifiPadrao: comPadrao })}
            vincularPlaca={can('robo', 'configure') ? async (mac) => {
              // Spec 2026-09-24-robo-painel-de-ajustes, 3.2: o MAC lido vai para o
              // cadastro, com trilha. Recusa vira aviso; o resto não para a configuração.
              try {
                await vincularPlacaAoCadastro(robo.public_id, mac)
                recarrega()
                return null
              } catch (erro) {
                return codigoDaRecusa(erro)
              }
            } : undefined} />
        </DataCard>
      )}

      {podeGerir && robo.status !== 'retired' && (
        <DataCard title="Firmware">
          <Stack gap="lg">
            <Stack gap="xs">
              <Text size="sm" fw={600}>Pelo ar</Text>
              <AtualizarPeloAr robo={robo} modelos={modelos} />
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={600}>Pela USB</Text>
              <GravarFirmware robo={robo} aoConcluir={recarrega} />
            </Stack>
          </Stack>
        </DataCard>
      )}

      {/* Diagnostico so' le a serial: vale tambem para robo aposentado. */}
      {podeGerir && (
        <DataCard title="Diagnóstico pela USB">
          <DiagnosticoPelaUsb robo={robo} />
        </DataCard>
      )}

      {/* Porta propria (spec 2026-09-24-robo-wifi, 4.2): a senha passa pelo
          console, e quem pode gerir a frota nao ganha isso junto. */}
      {can('robo', 'configure') && robo.status !== 'retired' && (
        <DataCard title="Wi-Fi">
          <WifiDoRobo robo={robo} endereco={enderecoDoWifiDaFrota(robo.public_id)}
            semSinal={e.chave !== 'ativo' && e.chave !== 'atrasado'} />
        </DataCard>
      )}

      {/* Mesma porta do Wi-Fi (`robo:configure`), e pelo mesmo motivo: quem pode
          trocar a rede do robo e' quem pode manda'-lo esquecer todas. */}
      {can('robo', 'configure') && robo.status !== 'retired' && (
        <DataCard title="Recomeçar a configuração">
          <ComecarDeNovo robo={robo} onMudou={recarrega}
            semSinal={e.chave !== 'ativo' && e.chave !== 'atrasado'} />
        </DataCard>
      )}

      <DataCard title="Notificações no display">
        <Stack gap="sm">
          <Callout variant="info">Quem configura é o dono do robô.</Callout>
          {carregandoNotificacoes ? (
            <Stack gap="xs">
              <Skeleton h={20} />
              <Skeleton h={20} />
            </Stack>
          ) : notificacoesAtivas.length === 0 ? (
            <Text size="sm" c="dimmed">Nada configurado ainda.</Text>
          ) : (
            notificacoesAtivas.map((s) => (
              <Linha key={s.source} rotulo={sourceLabel(s.source)}>
                <Text size="sm">
                  {SEVERITY_OPTIONS.find((o) => o.value === s.min_severity)?.label ?? s.min_severity}
                  {' · '}
                  {rotuloEscopoSomenteLeitura(s)}
                </Text>
              </Linha>
            ))
          )}
        </Stack>
      </DataCard>

      <Modal opened={editando} onClose={ctlEditar.close} title="Editar robô">
        <Stack gap="sm">
          <TextInput
            label="Número de série do inventário"
            description="Identifica o robô na frota. O dono pode dar outro nome ao robô dele."
            value={rascunho.name}
            onChange={(ev) => setRascunho((r) => ({ ...r, name: ev.currentTarget.value }))}
          />
          <SeletorDeLocal
            value={rascunho.location}
            onChange={(location) => setRascunho((r) => ({ ...r, location }))}
          />
          {/* O PATCH trata ausencia como "nao mexer", entao modelo registrado
              nao volta a "nao informado" por aqui: so' se troca por outro. */}
          <SeletorDeModelo
            value={rascunho.modelo}
            limpavel={robo.hardware_model == null}
            onChange={(modelo) => setRascunho((r) => ({ ...r, modelo }))}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={ctlEditar.close}>Cancelar</Button>
            <Button
              onClick={async () => {
                await alterarRobo(robo.public_id, {
                  name: rascunho.name,
                  location: rascunho.location || null,
                  ...(rascunho.modelo ? { hardware_model: rascunho.modelo } : {}),
                })
                ctlEditar.close()
                recarrega()
              }}
            >
              Salvar
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={confirmarAposentar} onClose={ctlAposentar.close} title="Aposentar robô">
        <Stack gap="sm">
          {/* Confirmacao NOMEANDO o robo: aposentar o errado numa lista de
              nomes parecidos e' o erro previsivel aqui, e a credencial nao volta. */}
          <Text size="sm">
            Aposentar <strong>{nomeOuFallback(robo)}</strong> tira o robô da frota e invalida
            a credencial dele. Ele para de falar com o Console.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={ctlAposentar.close}>Cancelar</Button>
            <Button
              color="red"
              onClick={async () => {
                await aposentarRobo(robo.public_id)
                ctlAposentar.close()
                recarrega()
              }}
            >
              Aposentar robô
            </Button>
          </Group>
        </Stack>
      </Modal>

      <CredencialModal credencial={emitida} onClose={() => setEmitida(null)} />
    </Stack>
  )
}
